import QUERY_KEYS from '@/constants/queryKeys';
import { getApi } from '@/dependencies/burrbear-api';
import { TokenPriceData } from '@/services/burrbear-api/graphql/generated/api-types';
import { TokenInfoMap } from '@/types/TokenList';
import { getAddress } from '@ethersproject/address';
import { useQuery, UseQueryOptions } from '@tanstack/vue-query';
import { reactive, Ref, ref } from 'vue';
import useNetwork from '../useNetwork';
import { oneMinInMs } from '../useTime';

/**
 * TYPES
 */
export type TokenPrices = { [address: string]: number };
type QueryResponse = TokenPrices;
type QueryOptions = UseQueryOptions<QueryResponse>;

/**
 * Fetches token prices for all provided addresses.
 */
export default function useTokenPricesQuery(
  pricesToInject: Ref<TokenPrices> = ref({}),
  tokenList: Ref<TokenInfoMap> = ref({}),
  options: QueryOptions = {}
) {
  const { networkId } = useNetwork();
  const queryKey = reactive(
    QUERY_KEYS.Tokens.Prices(networkId, pricesToInject, tokenList)
  );

  function pricesArrayToMap(prices: TokenPriceData[]): TokenPrices {
    const tokenPrices = prices.reduce(
      (obj, item) => (
        (obj[getAddress(item.address)] = item.latestUSDPrice), obj
      ),
      {}
    );

    return tokenPrices;
  }

  function injectCustomTokens(
    prices: TokenPrices,
    pricesToInject: TokenPrices
  ): TokenPrices {
    for (const address of Object.keys(pricesToInject)) {
      prices[address] = pricesToInject[address];
    }
    return prices;
  }

  const api = getApi();
  const queryFn = async () => {
    const tokenAddresses = Object.keys(tokenList.value);
    console.log('Fetching', tokenAddresses.length, 'token prices');

    const { prices } = await api.GetCurrentTokenPrices({
      addresses: tokenAddresses,
    });

    let pricesMap = pricesArrayToMap(prices);
    pricesMap = injectCustomTokens(pricesMap, pricesToInject.value);

    return pricesMap;
  };

  const queryOptions = reactive({
    enabled: true,
    refetchInterval: oneMinInMs * 5,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    ...options,
  });

  return useQuery<QueryResponse>(
    queryKey,
    queryFn,
    queryOptions as QueryOptions
  );
}
